import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import {
  EVENT_TYPE_AUTHORIZATION_REQUEST,
  EVENT_TYPE_CURRENT_BASED_OA_CONFIRMED,
  EVENT_TYPE_END_CHARGING_SESSION,
  EVENT_TYPE_EV_CONNECTED,
  EVENT_TYPE_EV_DISCONNECTED,
  EVENT_TYPE_FAULT,
  EVENT_TYPE_FINISH_APPLYING_LIMIT,
  EVENT_TYPE_LOCAL_OA_CONFIRMED,
  EVENT_TYPE_NUMBER_OF_PHASES_CHANGED,
  EVENT_TYPE_OFFLINE,
  EVENT_TYPE_ONLINE,
  EVENT_TYPE_POWER_BASED_OA_CONFIRMED,
  EVENT_TYPE_START_APPLYING_LIMIT,
  EVENT_TYPE_START_CHARGING_SESSION,
  EVENT_TYPE_START_DISCHARGING_SESSION,
} from '../../../../PlazaView/components/LogEvents/helper';
import { mockChargingStations } from '../../../mocks/demoEdgeMockChargers';

const generateTimestamp = (hoursAgo = 24) => {
  const now = moment();
  const randomMinutes = Math.floor(Math.random() * hoursAgo * 60);
  return now.subtract(randomMinutes, 'minutes').valueOf();
};

const generateSessionId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array(12)
    .fill(0)
    .map(() => chars[Math.floor(Math.random() * chars.length)])
    .join('');
};

const eventValueGenerators = {
  [EVENT_TYPE_EV_CONNECTED]: () => ({
    current_requested: Math.floor(Math.random() * 50) + 50,
    number_of_phases: Math.random() > 0.5 ? 3 : 1,
  }),
  [EVENT_TYPE_EV_DISCONNECTED]: () => ({}),
  [EVENT_TYPE_AUTHORIZATION_REQUEST]: () => ({
    current_requested: Math.floor(Math.random() * 50) + 50,
    energy_requested: Math.floor(Math.random() * 50) + 10,
  }),
  [EVENT_TYPE_START_APPLYING_LIMIT]: () => ({
    current_limit: Math.floor(Math.random() * 30) + 60,
  }),
  [EVENT_TYPE_FINISH_APPLYING_LIMIT]: () => ({
    current_limit: Math.floor(Math.random() * 30) + 60,
  }),
  [EVENT_TYPE_START_CHARGING_SESSION]: () => {
    const startTime = Math.floor(Date.now() / 1000) - Math.floor(Math.random() * 3600);
    return {
      session_id: generateSessionId(),
      session_type: Math.random() > 0.5 ? '15118_DC' : 'IEC_61851',
      start_time: startTime,
    };
  },
  [EVENT_TYPE_START_DISCHARGING_SESSION]: () => {
    const startTime = Math.floor(Date.now() / 1000) - Math.floor(Math.random() * 3600);
    return {
      session_id: generateSessionId(),
      session_type: '15118_DC',
      start_time: startTime,
    };
  },
  [EVENT_TYPE_END_CHARGING_SESSION]: () => {
    const startTime = Math.floor(Date.now() / 1000) - Math.floor(Math.random() * 7200);
    const duration = Math.floor(Math.random() * 3600) + 1800;
    return {
      session_id: generateSessionId(),
      session_type: Math.random() > 0.5 ? '15118_DC' : 'IEC_61851',
      start_time: startTime,
      end_time: startTime + duration,
      departure_time: startTime + duration + 300,
      energy_delivered: Math.floor(Math.random() * 50) + 20,
    };
  },
  [EVENT_TYPE_OFFLINE]: () => ({}),
  [EVENT_TYPE_ONLINE]: () => ({}),
  [EVENT_TYPE_FAULT]: () => ({
    fault_code: `ERR_${Math.floor(Math.random() * 900) + 100}`,
    fault_description: 'Communication error',
  }),
  [EVENT_TYPE_NUMBER_OF_PHASES_CHANGED]: () => ({
    old_number_of_phases: Math.random() > 0.5 ? 1 : 3,
    new_number_of_phases: Math.random() > 0.5 ? 3 : 1,
  }),
  [EVENT_TYPE_LOCAL_OA_CONFIRMED]: () => ({
    current_limit: Math.floor(Math.random() * 30) + 60,
  }),
  [EVENT_TYPE_CURRENT_BASED_OA_CONFIRMED]: () => ({
    current_limit: Math.floor(Math.random() * 30) + 60,
  }),
  [EVENT_TYPE_POWER_BASED_OA_CONFIRMED]: () => ({
    power_limit: Math.floor(Math.random() * 30) + 50,
  }),
};

const eventScenarios = [
  [
    EVENT_TYPE_EV_CONNECTED,
    EVENT_TYPE_AUTHORIZATION_REQUEST,
    EVENT_TYPE_START_APPLYING_LIMIT,
    EVENT_TYPE_FINISH_APPLYING_LIMIT,
    EVENT_TYPE_START_CHARGING_SESSION,
    EVENT_TYPE_END_CHARGING_SESSION,
    EVENT_TYPE_EV_DISCONNECTED,
  ],
  [EVENT_TYPE_EV_CONNECTED, EVENT_TYPE_AUTHORIZATION_REQUEST, EVENT_TYPE_EV_DISCONNECTED],
  [EVENT_TYPE_OFFLINE, EVENT_TYPE_ONLINE],
  [
    EVENT_TYPE_EV_CONNECTED,
    EVENT_TYPE_AUTHORIZATION_REQUEST,
    EVENT_TYPE_START_CHARGING_SESSION,
    EVENT_TYPE_FAULT,
    EVENT_TYPE_EV_DISCONNECTED,
  ],
  [EVENT_TYPE_EV_CONNECTED, EVENT_TYPE_AUTHORIZATION_REQUEST, EVENT_TYPE_START_DISCHARGING_SESSION, EVENT_TYPE_EV_DISCONNECTED],
  [EVENT_TYPE_START_APPLYING_LIMIT, EVENT_TYPE_FINISH_APPLYING_LIMIT, EVENT_TYPE_LOCAL_OA_CONFIRMED],
  [EVENT_TYPE_POWER_BASED_OA_CONFIRMED, EVENT_TYPE_CURRENT_BASED_OA_CONFIRMED],
];

export const generateMockEventsForPlaza = (plazaId, eventCount = 30) => {
  const chargers = mockChargingStations[plazaId] || [];

  if (chargers.length === 0) return [];

  const events = [];

  for (let i = 0; i < eventCount; i++) {
    const charger = chargers[Math.floor(Math.random() * chargers.length)];
    const eventScenario = eventScenarios[Math.floor(Math.random() * eventScenarios.length)];
    const eventType = eventScenario[Math.floor(Math.random() * eventScenario.length)];
    const timestamp = generateTimestamp(24);
    const values = eventValueGenerators[eventType] ? eventValueGenerators[eventType]() : {};

    events.push({
      uuid: uuidv4(),
      ts: timestamp,
      chargerUuid: charger.id,
      chargerName: charger.name,
      plazaEventType: eventType,
      values,
      createdAt: timestamp,
    });
  }

  return events.sort((a, b) => b.ts - a.ts);
};

export const getMockEventsForEdgeController = (plazaId) => {
  if (!plazaId) return [];
  return generateMockEventsForPlaza(plazaId);
};
